import React, { useCallback, type PropsWithChildren } from 'react';

import Tabs, { Tab, TabList, TabPanel } from '@atlaskit/tabs';
import type { OnChangeCallback, SelectedType } from '@atlaskit/tabs/types';

import { token } from '@atlaskit/tokens';
import { cssMap, cx } from '@atlaskit/css';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import Heading from '@atlaskit/heading';
import type { NewCoreIconProps } from '@atlaskit/icon';
import Image from '@atlaskit/image';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { useIntl, type MessageDescriptor } from '@atlassian/jira-intl';
import type { IllustrationFn } from '@atlassian/jira-illustration/src/ui/types.tsx';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';

const panelStyles = cssMap({
	logo: {
		// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
		borderRadius: token('radius.small', '4px'),
	},
	dynamicTab: {
		backgroundColor: token('elevation.surface'),
		height: '416px',
		overflow: 'hidden',
	},
	modalBorderRadius: {
		borderTopLeftRadius: token('radius.small'),
		borderBottomLeftRadius: token('radius.small'),
	},
	title: {
		paddingLeft: token('space.300'),
		paddingTop: token('space.400'),
	},
	panel: {
		// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
		borderRadius: token('radius.small', '3px'),
		font: token('font.heading.xxlarge'),
		marginBlockStart: token('space.200'),
	},
	panelNotRoadmap: {
		paddingLeft: token('space.300'),
	},
	tabsList: {
		paddingLeft: token('space.300'),
	},
	dynamicProjectName: {
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		display: 'table',
		tableLayout: 'fixed',
		width: '100%',
	},
});

const TabComponent = ({ label }: { label: string }) => <Tab>{label}</Tab>;

type DynamicTabProps = {
	onChange: (index: number) => void;
	selected: number;
	content: {
		templateTitle: MessageDescriptor;
		logo: string;
		features: {
			title: {
				id: string;
				defaultMessage: string;
				description: string;
			};
			icon: {
				(props: NewCoreIconProps): JSX.Element;
				displayName: string;
			};
			description: MessageDescriptor;
			image: IllustrationFn;
		}[];
	};
	dynamicTemplateName?: string;
	templateKey: string;
	inModal?: boolean;
};

export const TabContent = ({
	onChange,
	selected,
	content,
	dynamicTemplateName,
	inModal = true,
}: PropsWithChildren<DynamicTabProps>): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const handleChange: OnChangeCallback = useCallback(
		(index: SelectedType, analyticsEvent: UIAnalyticsEvent) => {
			onChange(index);
			fireUIAnalytics(analyticsEvent, 'featureItem', {
				index,
			});
		},
		[onChange],
	);

	return (
		<Stack
			xcss={cx(panelStyles.dynamicTab, inModal && panelStyles.modalBorderRadius)}
			space="space.200"
		>
			<Inline space="space.100" alignBlock="center" xcss={panelStyles.title}>
				<Image src={content.logo} alt="" />
				<Heading size="medium">
					<Box as="span" xcss={cx(panelStyles.dynamicProjectName)}>
						{dynamicTemplateName ?? formatMessage(content.templateTitle)}
					</Box>
				</Heading>
			</Inline>
			<Tabs onChange={handleChange} selected={selected} id="onboarding-modal-tooltip-tabs">
				<Box xcss={panelStyles.tabsList}>
					<TabList>
						{content.features.map((feature) => (
							<TabComponent key={feature.title.id} label={formatMessage(feature.title)} />
						))}
					</TabList>
				</Box>
				{content.features.map((feature) => (
					<TabPanel key={`${feature.title.id}-content`}>
						<Box
							xcss={cx(
								panelStyles.panel,
								feature.title.defaultMessage !== 'Roadmap' && panelStyles.panelNotRoadmap,
							)}
						>
							<feature.image alt="" />
						</Box>
					</TabPanel>
				))}
			</Tabs>
		</Stack>
	);
};
